import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Mail } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate } from 'react-router'
import { z } from 'zod'
import { PATHS } from '@/app/router/routes'
import { Button } from '@/shared/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import LayoutAuth from '../components/layout-auth'
import useAuth from '../hooks/use-auth'
import { passwordRecoveryV2Service } from '../password-recovery-v2.service'

const forgotPasswordSchema = z.object({
  email: z.string().trim().email('Ingresa un correo electrónico válido.').max(254)
})

type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordV2() {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<ForgotPasswordValues>({
    defaultValues: { email: '' },
    resolver: zodResolver(forgotPasswordSchema)
  })

  if (auth?.id) return <Navigate to={PATHS.admin.root} replace />

  const submit = async (values: ForgotPasswordValues) => {
    try {
      await passwordRecoveryV2Service.request({ email: values.email.trim().toLowerCase() })
      navigate(PATHS.auth.forgotPasswordSuccess, { replace: true })
    } catch (error) {
      handleMutationError(error)
    }
  }

  return (
    <LayoutAuth
      title="Recupera tu contraseña"
      description="Ingresa el correo asociado a tu cuenta. Por seguridad, siempre mostraremos la misma confirmación."
    >
      <form className="grid w-full grid-cols-1 gap-6" onSubmit={handleSubmit(submit)}>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <Field data-invalid={Boolean(fieldState.error)}>
              <FieldLabel htmlFor="staff-password-recovery-email">Correo electrónico</FieldLabel>
              <Input
                {...field}
                id="staff-password-recovery-email"
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                spellCheck={false}
              />
              <FieldDescription>
                Si la cuenta existe y está activa, recibirás un enlace válido por 30 minutos.
              </FieldDescription>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Mail className="mr-2 size-4" />
          )}
          {isSubmitting ? 'Enviando…' : 'Enviar enlace de recuperación'}
        </Button>
      </form>

      <Button variant="link" asChild>
        <Link to={PATHS.auth.login}>Volver a iniciar sesión</Link>
      </Button>
    </LayoutAuth>
  )
}
